import type {
  ProjectAssignee,
  ProjectBoard,
  ProjectColumn,
  ProjectMember,
  ProjectTask,
  ProjectTaskCreateInput,
} from '../types/project';
import { formatMinutes, parseCalendarDateInput } from './dateTime';
import { isUnlimitedTaskLimit, projectMemberRoleLabel } from './projectForm';
import {
  completedDateFieldError,
  resolveCompletedDate,
  type CompletedOnChoice,
} from './taskCompletionDate';

const DURATION = /^(\d{1,3}):([0-5]\d)$/;

export type TaskFormFieldErrors = {
  taskName?: string;
  member?: string;
  dueDate?: string;
  completedDate?: string;
  timeEstimate?: string;
  timeSpent?: string;
  subtasks?: string;
};

export type TaskSubtaskDraft = {
  key: string;
  name: string;
  assignedEmployeeIds: number[];
};

let taskDraftKey = 0;

export function nextTaskDraftKey(prefix: string): string {
  taskDraftKey += 1;
  return `${prefix}-${taskDraftKey}`;
}

export function columnRequiresTaskDetails(
  column: Pick<
    ProjectColumn,
    'projectColumnId' | 'isDefaultColumn' | 'isCompletedColumn' | 'displayOrder'
  >,
  columns: Array<
    Pick<
      ProjectColumn,
      'projectColumnId' | 'isDefaultColumn' | 'isCompletedColumn' | 'displayOrder'
    >
  >,
): boolean {
  const optional = [...columns]
    .filter(item => item.isDefaultColumn && !item.isCompletedColumn)
    .sort(
      (left, right) =>
        left.displayOrder - right.displayOrder ||
        (left.projectColumnId ?? 0) - (right.projectColumnId ?? 0),
    )[0];
  if (!optional?.projectColumnId || !column.projectColumnId) return true;
  return column.projectColumnId !== optional.projectColumnId;
}

export function applyColumnTaskRules<T extends ProjectColumn>(columns: T[]): T[] {
  return columns.map(column => ({
    ...column,
    requiresTaskDetails: columnRequiresTaskDetails(column, columns),
  }));
}

export function parseDurationInput(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = trimmed.match(DURATION);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

export function formatDurationInput(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) return '';
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

export function formatDurationCompact(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 1) return '';
  return formatMinutes(minutes);
}

export function isValidDurationInput(value: string, { allowEmpty = true } = {}): boolean {
  const trimmed = value.trim();
  if (!trimmed) return allowEmpty;
  return DURATION.test(trimmed);
}

export function memberOptionLabel(member: ProjectMember): string {
  return `${member.employeeName} - ${projectMemberRoleLabel(member.memberRole)}`;
}

export function assigneeSummary(
  assignees: ProjectAssignee[],
  fallbackName?: string | null,
): string | null {
  if (assignees.length) {
    return assignees.map(item => item.employeeName).join(', ');
  }
  return fallbackName?.trim() || null;
}

export function selectedMemberLabel(
  members: ProjectMember[],
  selectedIds: number[],
): string {
  if (!selectedIds.length) return 'Select';
  const names = selectedIds
    .map(id => members.find(member => member.employeeId === id)?.employeeName)
    .filter((name): name is string => Boolean(name));
  return names.length ? names.join(', ') : `${selectedIds.length} selected`;
}

export function toggleEmployeeId(selectedIds: number[], employeeId: number): number[] {
  return selectedIds.includes(employeeId)
    ? selectedIds.filter(id => id !== employeeId)
    : [...selectedIds, employeeId];
}

export function missingRequiredTaskDetails(input: {
  assignedEmployeeId?: number | null;
  assignedEmployeeIds?: number[];
  dueDate: string;
  timeEstimate: string;
}): string[] {
  const missing: string[] = [];
  const hasMember =
    (input.assignedEmployeeIds ?? []).some(id => id > 0) ||
    (input.assignedEmployeeId != null && input.assignedEmployeeId > 0);
  if (!hasMember) missing.push('Member');
  if (!parseCalendarDateInput(input.dueDate.trim())) missing.push('Due Date');
  const estimate = parseDurationInput(input.timeEstimate);
  if (estimate == null || estimate < 1) missing.push('Time Estimate');
  return missing;
}

export function requiredFieldsMessage(columnName: string, missing: string[]): string {
  return missing
    .map(field => `${field} is required for ${columnName} tasks.`)
    .join('\n');
}

export function moveBlockedMessage(columnName: string, missing: string[]): string {
  return `This task cannot be moved to ${columnName}.\n\nPlease add:\n${missing
    .map(field => `• ${field}`)
    .join('\n')}`;
}

export type TaskMovePlan =
  | { kind: 'noop' }
  | { kind: 'blocked'; title: string; message: string }
  | { kind: 'promptComplete' }
  | { kind: 'move' };

export function planTaskMove(input: {
  currentColumnId: number | null;
  column: {
    projectColumnId: number | null;
    columnName: string;
    taskCount: number;
    taskLimit: number | null;
    requiresTaskDetails: boolean;
    isCompletedColumn: boolean;
  };
  assignedEmployeeId?: number | null;
  assignedEmployeeIds?: number[];
  dueDate: string;
  timeEstimate: string;
}): TaskMovePlan {
  if (!input.column.projectColumnId || input.column.projectColumnId === input.currentColumnId) {
    return { kind: 'noop' };
  }
  if (
    !isUnlimitedTaskLimit(input.column.taskLimit) &&
    input.column.taskLimit != null &&
    input.column.taskCount >= input.column.taskLimit
  ) {
    return {
      kind: 'blocked',
      title: 'Task limit reached',
      message: `${input.column.columnName} already has ${input.column.taskCount} / ${input.column.taskLimit} tasks.`,
    };
  }
  if (input.column.isCompletedColumn) {
    return { kind: 'promptComplete' };
  }
  if (input.column.requiresTaskDetails) {
    const missing = missingRequiredTaskDetails({
      assignedEmployeeId: input.assignedEmployeeId,
      assignedEmployeeIds: input.assignedEmployeeIds,
      dueDate: input.dueDate,
      timeEstimate: input.timeEstimate,
    });
    if (missing.length) {
      return {
        kind: 'blocked',
        title: 'Cannot move task',
        message: moveBlockedMessage(input.column.columnName, missing),
      };
    }
  }
  return { kind: 'move' };
}

/** Optimistically move a task into another board column without waiting on the API. */
export function relocateTaskOnBoard(
  board: ProjectBoard,
  taskId: number,
  targetColumnId: number,
): ProjectBoard {
  let moved: ProjectTask | null = null;
  const withoutTask = board.columns.map(column => {
    const index = column.tasks.findIndex(task => task.taskId === taskId);
    if (index < 0) return column;
    moved = {
      ...column.tasks[index],
      projectColumnId: targetColumnId,
    };
    return {
      ...column,
      tasks: column.tasks.filter(task => task.taskId !== taskId),
      taskCount: Math.max(0, column.taskCount - 1),
    };
  });
  if (!moved) return board;
  const relocated = moved;
  return {
    ...board,
    columns: withoutTask.map(column => {
      if (column.projectColumnId !== targetColumnId) return column;
      if (column.tasks.some(task => task.taskId === taskId)) return column;
      return {
        ...column,
        tasks: [...column.tasks, relocated],
        taskCount: column.taskCount + 1,
      };
    }),
  };
}

export function validateCreateTaskForm(input: {
  taskName: string;
  assignedEmployeeId?: number | null;
  assignedEmployeeIds?: number[];
  dueDate: string;
  timeEstimate: string;
  /** Omitted by Create Task: new tasks start at zero and gain time via time logs. */
  timeSpent?: string;
  description: string;
  descriptionOpen: boolean;
  subtasks: TaskSubtaskDraft[];
  requiresTaskDetails: boolean;
  requireTimeSpent?: boolean;
  /** When creating directly in a completed column. */
  requireCompletedDate?: boolean;
  completedOn?: CompletedOnChoice;
  customCompletedDate?: string;
  columnName: string;
}): { error: string | null; fieldErrors: TaskFormFieldErrors; payload: ProjectTaskCreateInput | null } {
  const fieldErrors: TaskFormFieldErrors = {};
  const taskName = input.taskName.trim();
  if (!taskName) fieldErrors.taskName = 'Task name is required.';

  const assignedEmployeeIds = [
    ...new Set(
      (input.assignedEmployeeIds ??
        (input.assignedEmployeeId != null ? [input.assignedEmployeeId] : [])
      ).filter(id => id > 0),
    ),
  ];

  const dueDate = input.dueDate.trim();
  if (dueDate && !parseCalendarDateInput(dueDate)) {
    fieldErrors.dueDate = 'Due date is not a valid date.';
  }

  if (!isValidDurationInput(input.timeEstimate)) {
    fieldErrors.timeEstimate = 'Time estimate must use hh:mm format.';
  }
  if (input.timeSpent !== undefined && !isValidDurationInput(input.timeSpent)) {
    fieldErrors.timeSpent = 'Time spent must use HH:mm format.';
  }

  const namedSubtasks = input.subtasks.map(item => ({
    ...item,
    name: item.name.trim(),
  }));
  const filled = namedSubtasks.filter(item => item.name);
  if (namedSubtasks.some(item => !item.name) && namedSubtasks.length > 0) {
    fieldErrors.subtasks = 'Subtask name cannot be empty.';
  }
  const unique = new Set(filled.map(item => item.name.toLowerCase()));
  if (unique.size !== filled.length) {
    fieldErrors.subtasks = 'Subtask names must be unique.';
  }

  if (input.requiresTaskDetails) {
    const missing = missingRequiredTaskDetails({
      assignedEmployeeIds,
      dueDate,
      timeEstimate: input.timeEstimate,
    });
    if (missing.includes('Member')) {
      fieldErrors.member = `Member is required for ${input.columnName} tasks.`;
    }
    if (missing.includes('Due Date')) {
      fieldErrors.dueDate = `Due Date is required for ${input.columnName} tasks.`;
    }
    if (missing.includes('Time Estimate')) {
      fieldErrors.timeEstimate = `Time Estimate is required for ${input.columnName} tasks.`;
    }
  }

  if (input.requireTimeSpent) {
    const spentMinutes = parseDurationInput(input.timeSpent ?? '');
    if (
      (spentMinutes == null || spentMinutes < 1) &&
      !fieldErrors.timeSpent
    ) {
      fieldErrors.timeSpent = `Time Spent is required for ${input.columnName} tasks.`;
    }
  }

  let completedDate: string | null = null;
  if (input.requireCompletedDate) {
    const completedOn = input.completedOn ?? 'today';
    const customCompletedDate = input.customCompletedDate ?? '';
    const dateError = completedDateFieldError(completedOn, customCompletedDate);
    if (dateError) {
      fieldErrors.completedDate = dateError;
    } else {
      completedDate = resolveCompletedDate(completedOn, customCompletedDate);
    }
  }

  const error =
    fieldErrors.taskName ||
    fieldErrors.member ||
    fieldErrors.dueDate ||
    fieldErrors.completedDate ||
    fieldErrors.timeEstimate ||
    fieldErrors.timeSpent ||
    fieldErrors.subtasks ||
    null;
  if (error) return { error, fieldErrors, payload: null };

  const estimate = parseDurationInput(input.timeEstimate);
  const spent =
    input.timeSpent === undefined ? undefined : parseDurationInput(input.timeSpent);
  const description = input.descriptionOpen ? input.description.trim() : '';

  return {
    error: null,
    fieldErrors,
    payload: {
      projectColumnId: 0,
      taskName,
      assignedEmployeeId: assignedEmployeeIds[0] ?? null,
      assignedEmployeeIds,
      dueDate: dueDate || null,
      ...(input.requireCompletedDate ? { completedDate } : {}),
      timeEstimate: estimate && estimate > 0 ? formatDurationInput(estimate) : null,
      ...(spent === undefined
        ? {}
        : { timeSpent: formatDurationInput(spent ?? 0) || '00:00' }),
      description: description || null,
      subtasks: filled.map(item => ({
        subtaskName: item.name,
        assignedEmployeeIds: item.assignedEmployeeIds ?? [],
      })),
    },
  };
}
